import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { forgotPassword } from '../../Services/APIs/authentication';
import AuthLayout from './AuthLayout';

const errorClasses = "text-xs text-cv2-destructive mt-1";

const ForgotPassword = () => {
    const navigate = useNavigate();
    const [submitError, setSubmitError] = useState('');
    const [submitSuccess, setSubmitSuccess] = useState('');

    const initialValues = { email: '' };

    const validationSchema = Yup.object().shape({
        email: Yup.string().email('Invalid email').required('Email is required'),
    });

    const handleSubmit = async (values, { setSubmitting }) => {
        setSubmitError('');
        setSubmitSuccess('');
        try {
            const response = await forgotPassword({ authenticate_email: values.email });
            setSubmitSuccess(response.data?.message || 'A password reset code has been sent to your email.');
            navigate('/reset-password', { state: { email: values.email } });
        } catch (error) {
            setSubmitError(error?.response?.data?.message || error?.data?.message || 'Something went wrong. Please try again.');
        }
        setSubmitting(false);
    };

    return (
        <AuthLayout variant="signin">

                <div className="flex-1 flex flex-col justify-center">
                    <div className="mb-12">
                        <p className="cv2-auth-eyebrow font-spaceMono text-[9px] tracking-[1.4px] uppercase mb-3.5">
                            Account Recovery
                        </p>
                        <h1 className="font-aeonik font-normal text-[38px] leading-[44px] tracking-[-0.9px] text-cv2-foreground normal-case mb-3">
                            Reset Your<br />Password
                        </h1>
                        <p className="cv2-auth-subcopy font-aeonik text-sm leading-[22px] normal-case">
                            Enter the email on your account and we'll send you a code to reset your password.
                        </p>
                    </div>

                    {submitSuccess && (
                        <div className="text-sm text-green-500 bg-green-500/10 border border-green-500/30 rounded-md px-3 py-2 mb-4">
                            {submitSuccess}
                        </div>
                    )}
                    {submitError && (
                        <div className="cv2-auth-error-banner text-sm border rounded-md px-3 py-2 mb-4">
                            {submitError}
                        </div>
                    )}

                    <Formik
                        initialValues={initialValues}
                        validationSchema={validationSchema}
                        onSubmit={handleSubmit}
                    >
                        {({ isSubmitting }) => (
                            <Form className="flex flex-col">
                                <div className="cv2-auth-row border-t py-4">
                                    <label htmlFor="email" className="cv2-auth-label block font-spaceMono text-[9px] tracking-[1.2px] uppercase mb-[7px]">
                                        Email Address
                                    </label>
                                    <Field
                                        type="email"
                                        name="email"
                                        placeholder="you@practice.com"
                                        className="cv2-auth-input w-full bg-transparent border-0 outline-none font-aeonik text-[15px] text-cv2-foreground normal-case"
                                    />
                                    <ErrorMessage name="email" component="div" className={errorClasses} />
                                </div>

                                <div className="cv2-auth-row border-t" />

                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className="mt-7 w-full h-12 bg-claraview-secondary hover:brightness-110 disabled:opacity-50 transition-all rounded-[3px] font-spaceMono text-[11px] tracking-[1.2px] text-claraview-quaternary uppercase"
                                >
                                    Send Reset Code
                                </button>
                            </Form>
                        )}
                    </Formik>
                </div>

                <Link to="/login" className="cv2-auth-footer mt-10 text-center font-aeonik text-[13px] normal-case block">
                    ← Back to Login
                </Link>
        </AuthLayout>
    );
};

export default ForgotPassword;
